import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';

import {
    AutocompleteArrayInput,
    BooleanInput,
    DateInput,
    LongTextInput,
    ReferenceArrayInput,
    required,
    Title,
} from 'react-admin';
import moment from 'moment';

import {closeNotify, NotifyForm, showErrorNotify, showInfoNotify} from '../../components';
import env from '../../env';
import {downloadFilePost, printFile} from '../../components/FileDownload';
import TreeCheckBoxCustomInput from '../../components/TreeCheckBoxCustomInput';
import utils from '../../utils/utils';
import axiosInstance from '../../api/axios';

const TbThuKhongQuaCongThanhToan = (props) => {
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);

    // Lưu current form values do NotifyForm (redux-form) cung cấp mỗi lần validate
    // Dùng ref để luôn có giá trị mới nhất khi người dùng bấm các nút hành động
    const formValuesRef = useRef({
        billMonth: moment().format('YYYY-MM-DD'),
        displayDueDate: false,
        showPrint: false,
        note: '',
        customerIds: [],
        studentEnrollmentIds: [],
    });

    // initial params chuyển cho NotifyForm (tương đương this.state cũ)
    const initialParams = useMemo(
        () => ({
            billMonth: moment().format('YYYY-MM-DD'),
            displayDueDate: false,
            showPrint: false,
            note: '',
            customerIds: [],
            studentEnrollmentIds: [],
        }),
        []
    );

    useEffect(() => {
        let mounted = true;
        axiosInstance
            .get('/api/customersByUnits?studentOnly=true')
            .then((rs) => {
                if (!mounted) return;
                setCustomers(rs?.data?.content || []);
            })
            .catch(() => {
                if (!mounted) return;
                setCustomers([]);
            });
        return () => {
            mounted = false;
        };
    }, []);

    const validateCustomerIds = useCallback(() => {
        const v = formValuesRef.current || {};
        const a = Array.isArray(selectedCustomerIds) ? selectedCustomerIds : [];
        const b = Array.isArray(v.studentEnrollmentIds) ? v.studentEnrollmentIds : [];
        if (a.length === 0 && b.length === 0) {
            showErrorNotify('Vui lòng chọn danh sách học sinh');
            return false;
        }
        return true;
    }, [selectedCustomerIds]);

    const getReportUrl = useCallback(
        async (fullDay, allStudent) => {
            const v = formValuesRef.current || {};
            setLoading(true);
            try {
                const data = {
                    fullDayOnly: fullDay,
                    allStudent: allStudent,
                    displayDueDate: !!v.displayDueDate,
                    note: v.note || '',
                    billMonth: v.billMonth,
                    showPrint: !!v.showPrint,
                    customerIds: selectedCustomerIds || [],
                    studentEnrollmentIds: v.studentEnrollmentIds || [],
                };
                if (v.showPrint) {
                    await printFile({
                        url: `${env.baseURL}/reports/tb-thu-khong-qua-cong-thanh-toan`,
                        method: 'POST',
                        data: data,
                        onStart: () => showInfoNotify('Đang tải file, vui lòng chờ...'),
                        onDone: () => closeNotify(),
                        onError: (e) => showErrorNotify('In thất bại: ' + (e?.message || 'Unknown error'))
                    });
                } else {
                    await downloadFilePost(
                        `${env.baseURL}/reports/tb-thu-khong-qua-cong-thanh-toan`,
                        'tb_thu_phi.pdf',
                        data
                    );
                }

            } finally {
                setLoading(false);
            }
        },
        [selectedCustomerIds]
    );

    const notifyAllStudents = useCallback(() => {
        if (!validateCustomerIds()) return;
        getReportUrl(false, true);
    }, [getReportUrl, validateCustomerIds]);

    const fullDayOnly = useCallback(() => {
        if (!validateCustomerIds()) return;
        getReportUrl(true, false);
    }, [getReportUrl, validateCustomerIds]);

    const notFullDayOnly = useCallback(() => {
        if (!validateCustomerIds()) return;
        getReportUrl(false, false);
    }, [getReportUrl, validateCustomerIds]);

    // validate từ redux-form (NotifyForm) sẽ được gọi khi người dùng thay đổi form
    // ta tranh thủ đồng bộ values vào ref để các handler luôn có state mới nhất
    const validate = useCallback((values) => {
        formValuesRef.current = {...formValuesRef.current, ...(values || {})};
        return {};
    }, []);

    return (
        <Card>
            <Title title="resources.tb-thu-khong-qua-cong-thanh-toan.name"/>
            <CardContent>
                <p>Ghi chú: Mẫu báo cáo 1 tờ A4 2 phiếu thu, dành cho trường hợp không sử dụng cổng thanh toán, hiển
                    thị toàn bộ nợ còn thiếu đến tháng được chọn</p>
                <NotifyForm
                    validate={validate}
                    allClazzes={notifyAllStudents}
                    fullDayClazzes={fullDayOnly}
                    notFullDayClazzes={notFullDayOnly}
                    params={initialParams}
                    isLoading={loading}
                >
                    {/* Dòng chọn tháng */}
                    <Grid container spacing={8}>
                        <Grid item xs={12} sm={6}>
                            <DateInput
                                label="resources.common.month"
                                source="billMonth"
                                validate={required()}
                                {...props}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}/>
                    </Grid>

                    {/* Các tuỳ chọn hiển thị */}
                    <Grid container spacing={8}>
                        <Grid item xs={12} sm={6}>
                            <BooleanInput
                                label="resources.tb-thu-khong-qua-cong-thanh-toan.displayDueDate"
                                source="displayDueDate"
                                {...props}
                            />
                        </Grid>
                        {/* Chọn học sinh theo enrollment (studentEnrollmentIds) */}
                        <Grid item xs={12} sm={6}>
                            <ReferenceArrayInput
                                source="studentEnrollmentIds"
                                reference="studentEnrollments"
                                label={'resources.studentEnrollments.name'}
                                perPage={20}
                                style={{minWidth: 200}}
                                filter={{filterPath: 'filterFullInfo'}}
                                {...props}
                            >
                                <AutocompleteArrayInput optionText={({
                                                                         customerId,
                                                                         customerName,
                                                                         unitName
                                                                     }) => `${customerId} - ${customerName}${unitName ? ' - ' + unitName : ''}`}/>
                            </ReferenceArrayInput>
                        </Grid>
                    </Grid>

                    {/* Ghi chú và danh sách học sinh theo cây (customerIds) */}
                    <Grid container spacing={8}>
                        <Grid item xs={12} sm={6}>
                            <LongTextInput
                                label="resources.tb-thu-khong-qua-cong-thanh-toan.note"
                                rows={5}
                                style={{maxWidth: '90%'}}
                                multiline
                                source="note"
                                {...props}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <label>
                                <b>Danh sách học sinh</b>
                            </label>
                            <div
                                style={{
                                    width: 500,
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                    marginTop: 10,
                                }}
                            >
                                <TreeCheckBoxCustomInput
                                    data={customers}
                                    expandTopLevel={true}
                                    onChange={(value) =>
                                        setSelectedCustomerIds(utils.extractCustomerIdsV2(value) || [])
                                    }
                                />
                            </div>
                        </Grid>
                    </Grid>
                    <Grid container spacing={8}>
                        <Grid item xs={12} sm={6}>
                            <BooleanInput
                                label="resources.tb-thu-khong-qua-cong-thanh-toan.showPrint"
                                source="showPrint"
                                {...props}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        </Grid>
                    </Grid>

                </NotifyForm>
            </CardContent>
        </Card>
    );
};

export default TbThuKhongQuaCongThanhToan;
